class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.includes(previousTerm)) {
        searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
      }
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,article&resources[limit]=4&section_id=predictive-search`,
        {
          signal: this.abortController.signal,
        }
      );
      if (!response.ok) throw new Error(response.status);

      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search').innerHTML;

      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) {
        // Code 20 is the AbortError
        return;
      }
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', true);
    const searchBox = this.querySelector('[data-predictive-search]');
    if (searchBox) searchBox.setAttribute('aria-busy', 'true');
  }

  setLiveRegionText(statusText) {
    this.querySelector('.predictive-search-status').innerHTML = statusText;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const searchBox = this.querySelector('[data-predictive-search]');
    if (searchBox) searchBox.setAttribute('aria-busy', 'false');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  getResultsMaxHeight() {
    const searchModalContent = document.querySelector('.search-modal__content');
    const isMobile = window.innerWidth < 750;
    
    if (isMobile) {
      // For mobile, limit the results height to fit better on small screens
      const modalTop = searchModalContent.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - modalTop - 20;
      
      // Use a percentage of the available height to ensure the results don't fill the entire screen
      this.resultsMaxHeight = Math.min(availableHeight * 0.7, 300);
    } else {
      // For desktop, use the original calculation
      this.resultsMaxHeight = window.innerHeight - searchModalContent.getBoundingClientRect().top - 20;
    }
    
    return this.resultsMaxHeight;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement) {
      this.input.setAttribute('aria-activedescendant', activeElement.id);
      activeElement.setAttribute('aria-selected', true);
      if (this.predictiveSearchResults.scrollTop > activeElement.offsetTop) {
        this.predictiveSearchResults.scrollTop = activeElement.offsetTop;
      } else if (this.predictiveSearchResults.scrollTop + this.predictiveSearchResults.clientHeight < activeElement.offsetTop + activeElement.offsetHeight) {
        this.predictiveSearchResults.scrollTop = activeElement.offsetTop + activeElement.offsetHeight - this.predictiveSearchResults.clientHeight;
      }
    }
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }
}

customElements.define('predictive-search', PredictiveSearch);
